import { Navigate, Outlet } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { useAuth } from '@/features/auth/session';
import { AppLoader } from '@/shared/components/feedback/AppLoader';

/**
 * Keeps a signed-in person off the signed-out screens.
 *
 * <p>`ProtectedRoute` in reverse, and the pair is the whole rule: a route belongs to people
 * with a session or to people without one, and typing its path is not a way around either.
 * Somebody signed in who types `/login` was shown the sign-in form — which is the thing no
 * product does, because at best it is confusing and at worst they sign in again as somebody
 * else without meaning to.
 *
 * <h2>It does not wait, and `ProtectedRoute` does — deliberately</h2>
 *
 * The two are asking different questions, and only one of them needs the server.
 *
 * <p>**Turning somebody away** needs only a token in storage. Whatever the server will say
 * about it, this browser is not one with no session, so the sign-in form is not for it. That
 * answer is available on the first render, which is why nobody sees a loader on the way to
 * the dashboard any more.
 *
 * <p>**Letting somebody in** needs `/account/me`, and `ProtectedRoute` waits for it. A token
 * can be expired, revoked, or left by an older version of this application; deciding on it
 * would be the split those two guards used to have — one asking storage, the other asking
 * the server — which is how a browser ended up with the dashboard AND the sign-in form.
 *
 * <p>The awkward case is a token the server has since rejected, and it costs exactly one
 * bounce: here to the dashboard, whose guard takes the 401, forgets the session, and sends
 * them back with storage empty. The second pass finds no token and the form renders. It
 * cannot loop, because the 401 removed the thing that caused the redirect.
 *
 * <p>`status === 'unknown'` still has a branch below. It is reached only by somebody with no
 * token whose session is somehow mid-flight — belt and braces rather than a case anybody has
 * seen.
 *
 * <h2>Silent, and `replace`</h2>
 *
 * No message. "You are already signed in" is a sentence nobody needs, on a page they did not
 * mean to be on. `replace` so the browser's Back button does not return them to the form and
 * bounce them forwards again — the same reason `AddTeamMemberPage` uses it.
 */
export function GuestRoute() {
  const { status, hasStoredSession } = useAuth();

  /*
   * A TOKEN IS ENOUGH TO TURN SOMEBODY AWAY, and nothing here waits to find out more.
   *
   * These screens are for people with no session. A token in storage says this browser is
   * not that, whatever the server will eventually say about it — so the redirect is not in
   * doubt and there is nothing to wait for. Waiting put a loader in front of it on every
   * one of them.
   *
   * <p>It is the only place that flag may be read this way, and it works only because it
   * turns people away rather than letting them in: see `hasStoredSession`. A dead token
   * costs one bounce -- to the dashboard, whose guard gets the 401, forgets the session,
   * and returns them here with storage empty.
   */
  if (hasStoredSession || status === 'signed-in') {
    return <Navigate to={ROUTES.dashboard} replace />;
  }

  if (status === 'unknown') {
    return <AppLoader />;
  }

  return <Outlet />;
}
